import { useQuery } from "@tanstack/react-query";
import { officePresenceKeys } from "@/lib/query";

export type OfficePresencePeer = {
  name: string;
  shirt: string;
  skin: string;
  zoneId: string;
  status: string;
};

export type OfficePresence = {
  count: number;
  peers: OfficePresencePeer[];
};

async function parseError(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error: unknown }).error;
      if (typeof err === "string") return err;
    }
  } catch {
    // ignore
  }
  return res.statusText || "Request failed";
}

/**
 * Poll who's in HQ for the ready-room presence strip.
 * Server reads the in-process multiplayer room — empty when no one is joined.
 */
export function useOfficePresence() {
  return useQuery({
    queryKey: officePresenceKeys.list(),
    queryFn: async (): Promise<OfficePresence> => {
      const res = await fetch("/api/office/presence", { credentials: "include" });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) {
        return { count: 0, peers: [] };
      }
      const data = (body as { data: unknown }).data;
      if (!data || typeof data !== "object") return { count: 0, peers: [] };
      const row = data as Record<string, unknown>;
      const peersRaw = Array.isArray(row.peers) ? row.peers : [];
      const peers: OfficePresencePeer[] = [];
      for (const p of peersRaw) {
        if (!p || typeof p !== "object") continue;
        const o = p as Record<string, unknown>;
        if (typeof o.name !== "string") continue;
        peers.push({
          name: o.name,
          shirt: typeof o.shirt === "string" ? o.shirt : "#1E6CC7",
          skin: typeof o.skin === "string" ? o.skin : "#E9BE93",
          zoneId: typeof o.zoneId === "string" ? o.zoneId : "commons",
          status: typeof o.status === "string" ? o.status : "available",
        });
      }
      return {
        count: typeof row.count === "number" ? row.count : peers.length,
        peers,
      };
    },
    refetchInterval: 8_000,
    staleTime: 4_000,
  });
}
